import { List, Map } from "immutable";
import { Observable, observable } from "micro-observables";
import { Signal } from "micro-signals";
import { v4 as uuid } from "uuid";
export interface Store<T extends { id: string }> {
	itemsById: Observable<Map<string, T>>;
	getObservable(id: string): Observable<T | null>;
	save(value: T): void;
	update(key: string, updater: (current: T) => T): void;
	merge(newItems: List<T>): void;
	remove(id: string): void;
	fetch(id: string): Promise<void>;
}
export class SimpleStore<T extends { id: string }> implements Store<T> {
	private _primaryKey: keyof T;
	private _populate?: (items: Observable<Map<string, T>>) => Observable<Map<string, T>>;
	private _itemsById = observable(Map<string, T>());
	onNewElements = new Signal<{ updateId: string; content: List<T> }>();
	private _bindedStore?: SimpleStore<T>;

	constructor(
		private readonly _fetch: (id: string) => Promise<T>,
		options?: {
			populate?: (items: Observable<Map<string, T>>) => Observable<Map<string, T>>;
			primaryKey?: keyof T;
		}
	) {
		this._populate = options?.populate;
		this._primaryKey = options?.primaryKey ?? "id";
	}

	get itemsById(): Observable<Map<string, T>> {
		if (!this._populate) {
			return this._itemsById;
		}
		return this._populate(this._itemsById);
	}

	getObservable(id: string): Observable<T | null> {
		return this.itemsById.transform((items) => items.get(id, null));
	}

	save(value: T, updateId: string = uuid()): void {
		this._itemsById.update((items) => items.set(`${value[this._primaryKey]}`, value));
		this.onNewElements.dispatch({ updateId, content: List([value]) });
	}

	update(key: string, updater: (current: T) => T): void {
		const item = this._itemsById.get().get(key);
		if (item) {
			this._itemsById.update((items) => items.update(key, updater));
		}
	}

	merge(newItems: List<T>, updateId: string = uuid()): void {
		this._itemsById.update((currentItems) =>
			currentItems.merge(newItems.map((item) => [`${item[this._primaryKey]}`, item]))
		);
		this.onNewElements.dispatch({ updateId, content: newItems });
	}

	remove(id: string): void {
		this._itemsById.update((items) => items.remove(id));
	}

	async fetch(id: string): Promise<void> {
		const result = await this._fetch(id);
		this._itemsById.update((items) => items.set(id, result));
		this.onNewElements.dispatch({ updateId: uuid(), content: List([result]) });
		this._bindedStore?.save(result);
	}

	/** Bind this store to another
	 * When this store fetch an object, it will save it to the binded store
	 * When the binded store is updated, it will save the new objects to this store
	 */
	bind(otherStore: SimpleStore<T>): void {
		this._bindedStore?.onNewElements.remove(this);
		this._bindedStore = otherStore;
		otherStore._itemsById.onChange((elements) => this.merge(List(elements.values())));
	}
}
